/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AttributeDefinition,
  AttributeType,
  Category,
  Product,
  ProductImage,
  AIProductSuggestion,
  CreateProductRequest,
  AIExecuteResponse,
  APIResponse,
  CursorPaginatedResponse,
  AIAttributeTemplate,
  LabelPrintSettings
} from '../types';
import { apiRequest, apiFileUploadRequest, retryWithBackoff } from './api/client';
// Legacy AI attribute types - kept for backward compatibility with existing code
interface AIAttributeDefinition {
  id: string;
  organization_id: string;
  attribute_name: string;
  display_name: string;
  attribute_scope: string;
  data_type: string;
  possible_values: string[];
  description: string;
  ai_prompt: string;
  sort_order: number;
  sync_to_square: boolean;
  is_active: boolean;
  created_at: Date;
  updated_at: Date;
}

interface AIAttributeDefinitionCreate {
  attribute_name: string;
  display_name: string;
  attribute_scope: string;
  data_type: string;
  possible_values?: string[];
  description?: string;
  ai_prompt?: string;
  sort_order?: number;
  sync_to_square?: boolean;
  is_active?: boolean;
}

interface AIAttributeDefinitionUpdate {
  display_name?: string;
  attribute_scope?: string;
  data_type?: string;
  possible_values?: string[];
  description?: string;
  ai_prompt?: string;
  sort_order?: number;
  sync_to_square?: boolean;
  is_active?: boolean;
}

// Enum Definitions - database-backed definitions for enum values
export interface EnumDefinition {
  id: string;
  organization_id: string;
  type: string;
  name: string;
  description: string;
  sort_order: number;
  created_at?: string;
  updated_at?: string;
}

export interface EnumDefinitionCreate {
  type: string;
  name: string;
  description: string;
  sort_order?: number;
}

export interface EnumDefinitionUpdate {
  name?: string;
  description?: string;
  sort_order?: number;
}

export interface BulkUpsertRequest {
  definitions: EnumDefinitionCreate[];
}

export interface BulkUpsertResponse {
  created: number;
  updated: number;
}

// API Configuration - empty string means same origin (all services unified in backend)
// All APIs are now unified in the backend, accessed through /api/* routes
const SQUARE_API_URL = '';  // Use same origin
const AI_API_URL = '';      // Use same origin

// Build API base URL for Square operations
const SQUARE_API_V2_BASE = '/api/square';

// apiRequest, retryWithBackoff, and apiFileUploadRequest now live in
// ./api/client.ts (imported below) so the 401 silent-refresh interceptor
// (SC-060) covers every call site in this module instead of only a
// duplicate copy that never saw the interceptor.

// Transform AI API response to our internal format
async function transformAIResponse(apiResponse: any, existingAttributes: AttributeDefinition[]): Promise<{ response: AIExecuteResponse, newAttributes: AttributeDefinition[] }> {
  console.log('Transforming AI API response:', apiResponse);

  // Handle the actual response structure from backend
  // The backend returns: content, parsed_content, validated_response
  let aiData = apiResponse.parsed_content || apiResponse.validated_response || apiResponse;
  
  // Log what we received
  console.log('AI Data received:', aiData);
  console.log('Has square_projection?', !!aiData.square_projection);
  console.log('Has internal_record?', !!aiData.internal_record);
  
  // If we have a nested structure, use it directly
  if (aiData.square_projection || aiData.internal_record) {
    console.log('Found nested AI response structure - passing through directly');
    // The data is already in the correct format
  }

  // Process attributes from the AI response
  const processedAttributes: Record<string, string | string[]> = {};
  const newAttributesToCreate: AttributeDefinition[] = [];
  const attributesToUpdate: { attribute: AttributeDefinition, newSelectionOptions: string[] }[] = [];

  if (apiResponse.attributes && Array.isArray(apiResponse.attributes)) {
    console.log('Processing attributes:', apiResponse.attributes);

    for (const attr of apiResponse.attributes) {
      console.log('Processing attribute:', attr);

      const key = attr.key;
      const value = attr.value;

      if (!key || !value) {
        console.warn('Skipping invalid attribute:', attr);
        continue;
      }

      // Find existing attribute by name/key
      const existingAttr = existingAttributes.find(a =>
        a.name === key ||
        a.name.toLowerCase().replace(/\s+/g, '_') === key.toLowerCase() ||
        a.id === key
      );

      if (existingAttr) {
        console.log('Found existing attribute:', existingAttr.name);
        // Attribute exists - check if we need to add new selection options
        const values = value.includes(',') ? value.split(',').map((v: string) => v.trim().replace(/_/g, ' ')) : [value.replace(/_/g, ' ')];
        const newOptions = values.filter((v: string) => !existingAttr.selection_options?.includes(v));

        if (newOptions.length > 0) {
          console.log('Adding new selection options to existing attribute:', newOptions);
          // Need to update existing attribute with new selection options
          attributesToUpdate.push({
            attribute: existingAttr,
            newSelectionOptions: [...(existingAttr.selection_options || []), ...newOptions]
          });
        }

        // Set the attribute value for the product
        if (existingAttr.type === 'SELECTION' && values.length > 1) {
          processedAttributes[existingAttr.name] = values;
        } else {
          processedAttributes[existingAttr.name] = values[0] || value;
        }
      } else {
        console.log('Creating new attribute definition for key:', key);
        // Attribute doesn't exist - create new one
        const values = value.includes(',') ? value.split(',').map((v: string) => v.trim().replace(/_/g, ' ')) : [value.replace(/_/g, ' ')];
        const isMultiValue = values.length > 1;

        const newAttribute: Omit<AttributeDefinition, 'id' | 'created_at' | 'updated_at'> = {
          name: key.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()), // Convert snake_case to Title Case
          type: isMultiValue ? AttributeType.SELECTION : AttributeType.STRING,
          description: `Auto-created from AI analysis for ${key}`,
          allowed_object_types: ['ITEM'],
          selection_options: isMultiValue ? values : []
        };

        // We'll create this via API call later in the function
        console.log('Will create new attribute definition:', newAttribute);

        // Set the attribute value for the product
        if (isMultiValue) {
          processedAttributes[newAttribute.name] = values;
        } else {
          processedAttributes[newAttribute.name] = values[0] || value.replace(/_/g, ' ');
        }
      }
    }
  }

  // Create new attributes via API if needed
  for (const newAttr of newAttributesToCreate) {
    try {
      console.log('Creating new attribute:', newAttr);
      const createResult = await attributesApi.create(newAttr);

      if (!createResult.success) {
        console.warn('Backend does not support attribute creation:', newAttr.name, createResult.message);
        // Continue processing even if backend doesn't support attribute creation
      }
    } catch (error) {
      console.warn('Attribute creation endpoint not available:', newAttr.name, error);
      // Continue processing - the attribute will still be available in the UI
    }
  }

  // Update existing attributes with new selection options
  for (const { attribute, newSelectionOptions } of attributesToUpdate) {
    try {
      console.log('Updating attribute with new selection options:', attribute.name, newSelectionOptions);
      const updateResult = await attributesApi.update(attribute.id, {
        name: attribute.name,
        selection_options: newSelectionOptions,
      });

      if (!updateResult.success) {
        console.warn('Backend does not support attribute updates:', attribute.name, updateResult.message);
        // Continue processing even if backend doesn't support attribute updates
      }
    } catch (error) {
      console.warn('Attribute update endpoint not available:', attribute.name, error);
      // Continue processing - the attribute will still be available in the UI
    }
  }

  // Pass through the actual AI response data preserving the structure
  // The component will handle the dynamic field extraction
  const transformed: any = {
    id: Math.random().toString(),
    sku: `SKU-${Date.now()}`,
    attributes: processedAttributes,
    
    // Preserve the entire AI response structure
    ...aiData,  // This includes square_projection and internal_record if present
    
    // Also include the raw response fields for debugging
    parsed_content: apiResponse.parsed_content,
    validated_response: apiResponse.validated_response,
    content: apiResponse.content,
  };

  console.log('Transformed AI response:', transformed);
  console.log('New attributes created:', newAttributesToCreate.length);
  console.log('Attributes updated:', attributesToUpdate.length);

  return {
    response: transformed,
    newAttributes: [...newAttributesToCreate, ...attributesToUpdate.map(u => ({ ...u.attribute, selection_options: u.newSelectionOptions }))]
  };
}

// Helper function to map frontend attribute types to backend types
// Transform API attribute to our internal format
function transformApiAttribute(apiAttr: any): AttributeDefinition {
  console.log('Transforming v2 API attribute:', apiAttr);

  // Handle v2 API response format - selection_options is directly available
  const selectionOptions = Array.isArray(apiAttr.selection_options) ? apiAttr.selection_options :
    Array.isArray(apiAttr.values) ? apiAttr.values : []; // Fallback for v1 compatibility

  const transformed = {
    id: apiAttr.id || Math.random().toString(),
    name: apiAttr.name || 'Unknown',
    type: (apiAttr.type || 'STRING') as AttributeType, // Backend and frontend use same type values
    description: apiAttr.description || '',
    allowed_object_types: apiAttr.allowed_object_types || ['ITEM'],
    selection_options: selectionOptions,
    created_at: new Date(apiAttr.created_at || Date.now()),
    updated_at: new Date(apiAttr.updated_at || Date.now()),
  };

  console.log('Transformed v2 attribute result:', transformed);
  return transformed;
}

// Transform API category to our internal format
function transformApiCategory(apiCategory: any): Category {
  console.log('Transforming API category:', apiCategory);
  console.log('API category name:', apiCategory.name, 'Type:', typeof apiCategory.name);

  return {
    id: apiCategory.id?.toString() || Math.random().toString(),
    name: apiCategory.name ?? 'Unknown', // Use nullish coalescing to only default if null/undefined
    abbreviation: apiCategory.abbreviation || '',
    parent_category_id: apiCategory.parent_category_id || undefined,
    is_deleted: apiCategory.is_deleted || false,
    present_at_all_locations: apiCategory.present_at_all_locations || true,
    present_at_location_ids: apiCategory.present_at_location_ids || [],
    path_to_root: apiCategory.path_to_root || [],
    is_top_level: !apiCategory.parent_category_id,
    online_visibility: apiCategory.online_visibility || true,
    created_at: new Date(apiCategory.created_at || Date.now()),
    updated_at: new Date(apiCategory.updated_at || Date.now()),
  };
}

// Transform API product to our internal format
function transformApiProduct(apiProduct: any): Product {
  console.log('Raw Square API product:', apiProduct);
  console.log('Image data:', { 
    images: apiProduct.images, 
    image_ids: apiProduct.image_ids,
    item_data: apiProduct.item_data 
  });
  return {
    id: apiProduct.id?.toString() || Math.random().toString(),
    name: apiProduct.name || 'Unknown',
    description: apiProduct.description || '',
    status: apiProduct.status || 'ACTIVE',
    abbreviation: apiProduct.abbreviation,
    label_color: apiProduct.label_color,
    available_online: apiProduct.available_online !== undefined ? apiProduct.available_online : true,
    available_for_pickup: apiProduct.available_for_pickup !== undefined ? apiProduct.available_for_pickup : true,
    skip_modifier_screen: apiProduct.skip_modifier_screen !== undefined ? apiProduct.skip_modifier_screen : false,
    price_amount: apiProduct.price_amount || 0, // Backend already sends in cents
    currency: apiProduct.currency || 'USD',
    tax_ids: apiProduct.tax_ids || [],
    tax_exempt: apiProduct.tax_exempt !== undefined ? apiProduct.tax_exempt : false,
    inventory_quantity: apiProduct.inventory_quantity || undefined,
    category_id: apiProduct.category_id || undefined,
    categories: apiProduct.categories || [],  // Add missing categories field
    version: apiProduct.version,
    is_deleted: apiProduct.is_deleted !== undefined ? apiProduct.is_deleted : false,
    present_at_all_locations: apiProduct.present_at_all_locations !== undefined ? apiProduct.present_at_all_locations : true,
    present_at_location_ids: apiProduct.present_at_location_ids,
    attributes: apiProduct.attributes || [],
    image_ids: apiProduct.image_ids || [],
    tags: apiProduct.tags || [],
    sku: apiProduct.sku || '',
    images: (() => {
      // First check for image_urls from Square API
      if (apiProduct.image_urls && apiProduct.image_urls.length > 0) {
        console.log('✅ Using Square image URLs for', apiProduct.name, ':', apiProduct.image_urls);
        return apiProduct.image_urls.map((url: string, index: number) => ({ 
          url,
          id: apiProduct.image_ids?.[index] || `img-${index}`,
          type: index === 0 ? 'primary' : 'additional',
          label: `Image ${index + 1}`
        }));
      } else if (apiProduct.images && apiProduct.images.length > 0) {
        console.log('Using existing images:', apiProduct.images);
        return apiProduct.images;
      } else if (apiProduct.image_ids && apiProduct.image_ids.length > 0) {
        // Generate placeholder images that will be resolved by the image URL resolver
        console.log('⚠️ Only image IDs found (no URLs) for', apiProduct.name, ', will resolve URLs:', apiProduct.image_ids);
        return apiProduct.image_ids.map((imageId: string, index: number) => ({
          id: imageId,
          url: '', // Will be resolved by getImageUrl function
          type: index === 0 ? 'primary' : 'additional',
          label: `Image ${index + 1}`,
          needsUrlResolution: true
        }));
      }
      console.log('❌ No images or image_ids found for', apiProduct.name);
      return [];
    })(),
    updated_at: new Date(apiProduct.updated_at || Date.now()),
    created_at: new Date(apiProduct.created_at || Date.now()),
  };
}

// AI Attributes API (new unified system)
export const aiAttributesApi = {
  // Alias for backwards compatibility with ProductAddEdit.tsx
  async list(options: { organization_id?: string; is_active?: boolean; attribute_scope?: string; sync_to_square?: boolean } = {}): Promise<APIResponse<AIAttributeDefinition[]>> {
    return this.getAll(options.organization_id, options.attribute_scope, options.sync_to_square, options.is_active);
  },

  async getAll(organizationId?: string, scope?: string, syncToSquare?: boolean, isActive?: boolean): Promise<APIResponse<AIAttributeDefinition[]>> {
    const params = new URLSearchParams();
    if (organizationId) params.append('organization_id', organizationId);
    if (scope) params.append('attribute_scope', scope);
    if (syncToSquare !== undefined) params.append('sync_to_square', syncToSquare.toString());
    if (isActive !== undefined) params.append('is_active', isActive.toString());

    const endpoint = `/api/ai-attributes/${params.toString() ? '?' + params.toString() : ''}`;
    console.log('Fetching AI attributes from:', endpoint);

    const response = await apiRequest<AIAttributeDefinition[]>(endpoint, {}, 'ai');
    if (response.success && Array.isArray(response.data)) {
      // Transform dates
      const attributes = response.data.map(attr => ({
        ...attr,
        created_at: new Date(attr.created_at),
        updated_at: new Date(attr.updated_at),
      }));
      return { data: attributes, success: true };
    }
    return response as APIResponse<AIAttributeDefinition[]>;
  },

  async getById(id: string): Promise<APIResponse<AIAttributeDefinition | null>> {
    console.log('Fetching AI attribute by ID:', id);
    const response = await apiRequest<AIAttributeDefinition>(`/api/ai-attributes/${encodeURIComponent(id)}`, {}, 'ai');

    if (response.success && response.data) {
      return {
        data: {
          ...response.data,
          created_at: new Date(response.data.created_at),
          updated_at: new Date(response.data.updated_at),
        },
        success: true
      };
    }
    return { data: null, success: false, message: response.message || 'Attribute not found' };
  },

  async create(organizationId: string, data: AIAttributeDefinitionCreate): Promise<APIResponse<AIAttributeDefinition>> {
    console.log('Creating AI attribute for org:', organizationId, 'with data:', data);
    const response = await apiRequest<AIAttributeDefinition>(
      `/api/ai-attributes/?organization_id=${organizationId}`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      'ai'
    );

    if (response.success && response.data) {
      return {
        data: {
          ...response.data,
          created_at: new Date(response.data.created_at),
          updated_at: new Date(response.data.updated_at),
        },
        success: true
      };
    }
    return response as APIResponse<AIAttributeDefinition>;
  },

  async update(id: string, organizationId: string, data: AIAttributeDefinitionUpdate): Promise<APIResponse<AIAttributeDefinition>> {
    console.log('Updating AI attribute', id, 'for org:', organizationId, 'with data:', data);
    const response = await apiRequest<AIAttributeDefinition>(
      `/api/ai-attributes/${encodeURIComponent(id)}?organization_id=${organizationId}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      },
      'ai'
    );

    if (response.success && response.data) {
      return {
        data: {
          ...response.data,
          created_at: new Date(response.data.created_at),
          updated_at: new Date(response.data.updated_at),
        },
        success: true
      };
    }
    return response as APIResponse<AIAttributeDefinition>;
  },

  async delete(id: string, organizationId: string): Promise<APIResponse<boolean>> {
    console.log('Deleting AI attribute with ID:', id);
    const response = await apiRequest<any>(
      `/api/ai-attributes/${encodeURIComponent(id)}?organization_id=${organizationId}`,
      {
        method: 'DELETE',
      },
      'ai'
    );
    return { data: response.success, success: response.success, message: response.message };
  },

  async generatePrompt(organizationId: string): Promise<APIResponse<string>> {
    console.log('Generating AI prompt for org:', organizationId);
    const response = await apiRequest<{ prompt: string }>(
      `/api/ai-attributes/generate-prompt?organization_id=${organizationId}`,
      {},
      'ai'
    );

    if (response.success && response.data) {
      return { data: response.data.prompt, success: true };
    }
    return { data: '', success: false, message: response.message || 'Failed to generate prompt' };
  },

  async getTemplates(): Promise<APIResponse<AIAttributeTemplate[]>> {
    console.log('Fetching AI attribute templates');
    const response = await apiRequest<{ templates: AIAttributeTemplate[] }>(
      `/api/ai-attributes/templates`,
      {},
      'ai'
    );

    if (response.success && response.data) {
      return { data: response.data.templates, success: true };
    }
    return { data: [], success: false, message: response.message || 'Failed to fetch templates' };
  },

  async bulkCreateDefaults(organizationId: string, templateId: string = 'kimono'): Promise<APIResponse<AIAttributeDefinition[]>> {
    console.log('Bulk creating default AI attributes for org:', organizationId, 'with template:', templateId);
    const response = await apiRequest<AIAttributeDefinition[]>(
      `/api/ai-attributes/bulk-create-defaults?organization_id=${organizationId}&template_id=${templateId}`,
      {
        method: 'POST',
      },
      'ai'
    );

    if (response.success && Array.isArray(response.data)) {
      const attributes = response.data.map(attr => ({
        ...attr,
        created_at: new Date(attr.created_at),
        updated_at: new Date(attr.updated_at),
      }));
      return { data: attributes, success: true };
    }
    return response as APIResponse<AIAttributeDefinition[]>;
  },
};

// Legacy Attributes API (kept for backward compatibility)
export const attributesApi = {
  async getAll(): Promise<APIResponse<AttributeDefinition[]>> {
    console.log('⚠️ Using legacy attribute definitions - consider migrating to AI attributes API');
    console.log('Fetching all attributes from v2 API:', `/attribute-definitions?limit=50`);
    const response = await apiRequest<any>('/attribute-definitions?limit=50');
    if (response.success) {
      console.log('Raw attributes v2 response:', response.data);

      // Handle v2 API response structure: { items: [], total_count: number, cursor: string | null, has_more: boolean }
      if (response.data && response.data.items && Array.isArray(response.data.items)) {
        const attributes = response.data.items.map(transformApiAttribute);
        console.log('Transformed attributes:', attributes);
        return { data: attributes, success: true };
      } else {
        console.warn('Unexpected v2 API response structure:', response.data);
        return { data: [], success: true };
      }
    }
    return response as APIResponse<AttributeDefinition[]>;
  },

  async getById(key: string): Promise<APIResponse<AttributeDefinition | null>> {
    console.log('Fetching attribute by key:', key, 'from v2 API:', `/attribute-definitions/${key}`);
    const response = await apiRequest<any>(`/attribute-definitions/${encodeURIComponent(key)}`);
    console.log('Raw attribute v2 response:', response);

    if (response.success && response.data) {
      const attribute = transformApiAttribute(response.data);
      console.log('Transformed attribute:', attribute);
      return { data: attribute, success: true };
    }

    // If the API call failed, try to get from the list (fallback)
    console.log('Direct fetch failed, trying to find in list...');
    const allResponse = await this.getAll();
    if (allResponse.success && allResponse.data) {
      const found = allResponse.data.find(attr => attr.id === key || attr.name === key);
      if (found) {
        console.log('Found attribute in list:', found);
        return { data: found, success: true };
      }
    }

    return { data: null, success: false, message: response.message || 'Attribute not found' };
  },

  async create(data: Omit<AttributeDefinition, 'id' | 'created_at' | 'updated_at'>): Promise<APIResponse<AttributeDefinition>> {
    const payload = {
      name: data.name,
      type: data.type, // Backend accepts same type values as frontend
      description: data.description || '',
      allowed_object_types: data.allowed_object_types,
      selection_options: data.selection_options || []
    };

    console.log('Creating attribute with v2 API payload:', payload);
    const response = await apiRequest<any>('/attribute-definitions', {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    if (response.success) {
      const attribute = transformApiAttribute(response.data);
      return { data: attribute, success: true };
    }
    return response as APIResponse<AttributeDefinition>;
  },

  async update(id: string, data: Partial<Omit<AttributeDefinition, 'id' | 'created_at' | 'updated_at'>>): Promise<APIResponse<AttributeDefinition>> {
    const payload = {
      name: data.name || '',
      type: data.type || 'STRING', // Backend accepts same type values as frontend
      description: data.description || '',
      allowed_object_types: data.allowed_object_types || ['ITEM'],
      selection_options: data.selection_options || []
    };

    console.log('Updating attribute', id, 'with v2 API payload:', payload);
    const response = await apiRequest<any>(`/attribute-definitions/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });

    if (response.success) {
      const attribute = transformApiAttribute(response.data);
      return { data: attribute, success: true };
    }
    return response as APIResponse<AttributeDefinition>;
  },

  // Delete method removed - attribute definitions cannot be deleted
  // async delete(key: string): Promise<APIResponse<boolean>> { ... }
};

// Categories API
export const categoriesApi = {
  async getAll(limit: number = 50, cursor?: string, parent_id?: string, search?: string): Promise<APIResponse<Category[]>> {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    if (cursor) params.append('cursor', cursor);
    if (parent_id) params.append('parent_id', parent_id);
    if (search) params.append('search', search);

    const endpoint = `/categories?${params.toString()}`;
    console.log('Fetching categories from:', endpoint);

    const response = await apiRequest<any>(endpoint);
    if (response.success) {
      console.log('Raw categories response:', response.data);
      // Handle paginated response structure
      const items = response.data.items || response.data || [];
      const categories = Array.isArray(items) ? items.map(transformApiCategory) : [];
      console.log('Transformed categories:', categories);
      return { data: categories, success: true };
    }
    return response as APIResponse<Category[]>;
  },

  async getById(id: string): Promise<APIResponse<Category | null>> {
    console.log('Fetching category by ID:', id, 'from:', `/categories/${id}`);
    const response = await apiRequest<any>(`/categories/${encodeURIComponent(id)}`);
    console.log('Raw category response:', response);

    if (response.success && response.data) {
      // Handle paginated response structure
      if (response.data.items && Array.isArray(response.data.items)) {
        const items = response.data.items;
        if (items.length > 0) {
          const category = transformApiCategory(items[0]);
          console.log('Transformed category:', category);
          return { data: category, success: true };
        } else {
          return { data: null, success: false, message: 'Category not found' };
        }
      }
      // Handle direct category object (fallback for non-paginated responses)
      else {
        const category = transformApiCategory(response.data);
        console.log('Transformed category:', category);
        return { data: category, success: true };
      }
    }
    return { data: null, success: false, message: response.message };
  },

  async create(data: { name: string; abbreviation?: string; parent_category_id?: string }): Promise<APIResponse<Category>> {
    const payload = {
      name: data.name,
      abbreviation: data.abbreviation,
      parent_category_id: data.parent_category_id,
    };

    console.log('Creating category with payload:', payload);
    const response = await apiRequest<any>('/categories', {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    if (response.success) {
      const category = transformApiCategory(response.data);
      return { data: category, success: true };
    }
    return response as APIResponse<Category>;
  },

  async update(id: string, data: { name?: string; parent_category_id?: string }): Promise<APIResponse<Category>> {
    const payload = {
      name: data.name,
      parent_category_id: data.parent_category_id,
    };

    console.log('Updating category', id, 'with payload:', payload);
    const response = await apiRequest<any>(`/categories/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });

    if (response.success) {
      const category = transformApiCategory(response.data);
      return { data: category, success: true };
    }
    return response as APIResponse<Category>;
  },

  async delete(id: string): Promise<APIResponse<boolean>> {
    console.log('Deleting category with ID:', id);
    const response = await apiRequest<any>(`/categories/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
    return { data: response.success, success: response.success, message: response.message };
  },

  async getChildren(id: string, limit: number = 50): Promise<APIResponse<Category[]>> {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());

    const endpoint = `/categories/${encodeURIComponent(id)}/children?${params.toString()}`;
    console.log('Fetching category children from:', endpoint);

    const response = await apiRequest<any>(endpoint);
    if (response.success) {
      const items = response.data.items || response.data || [];
      const categories = Array.isArray(items) ? items.map(transformApiCategory) : [];
      return { data: categories, success: true };
    }
    return response as APIResponse<Category[]>;
  },

  async getProducts(id: string, limit: number = 50): Promise<APIResponse<Product[]>> {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());

    const endpoint = `/categories/${encodeURIComponent(id)}/products?${params.toString()}`;
    console.log('Fetching category products from:', endpoint);

    const response = await apiRequest<any>(endpoint);
    if (response.success) {
      const items = response.data.items || response.data || [];
      const products = Array.isArray(items) ? items.map(transformApiProduct) : [];
      return { data: products, success: true };
    }
    return response as APIResponse<Product[]>;
  },

  async bulkCreate(categories: { name: string; parent_category_id?: string }[]): Promise<APIResponse<any>> {
    console.log('Bulk creating categories with payload:', categories);
    const response = await apiRequest<any>('/categories/bulk', {
      method: 'POST',
      body: JSON.stringify(categories),
    });
    return response;
  },
};

// Products API
export const productsApi = {
  async getImageUrl(imageId: string): Promise<string> {
    try {
      console.log(`Fetching image URL for ID: ${imageId}`);
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      const imgToken = localStorage.getItem('auth_token');
      if (imgToken) {
        headers['Authorization'] = `Bearer ${imgToken}`;
      }

      const response = await fetch(`${SQUARE_API_V2_BASE}/images/${imageId}/url`, {
        method: 'GET',
        headers,
        credentials: 'include', // Include cookies for authentication
      });
      
      if (!response.ok) {
        console.error(`❌ HTTP ${response.status} when fetching image URL for ${imageId}`);
        return '';
      }
      
      const data = await response.json();
      console.log(`✅ Image URL response for ${imageId}:`, data);
      
      if (data.success && data.data?.url) {
        console.log(`✅ Resolved image URL for ${imageId}: ${data.data.url}`);
        return data.data.url;
      } else {
        console.error(`❌ No URL in response for ${imageId}:`, data);
        return '';
      }
    } catch (error) {
      console.error(`❌ Error fetching image URL for ${imageId}:`, error);
      return '';
    }
  },

  // SC-072: pages via the signed `cursor` query param the backend accepts
  // (SC-069/PR #73), not `page`/`page_size` - the backend never honored
  // those, which is why pagination past page 1 was a silent no-op. A
  // `cursor` from one query (search/category) must never be replayed
  // against a different one; the backend enforces this itself and returns
  // HTTP 400 on a cursor/query mismatch (surfaced via `status` for the
  // caller to reset to page 1, see useCursorPagination.ts). An options
  // object (rather than positional params) avoids miscalling a signature
  // with two optionals and a defaulted middle argument.
  async getAll({ cursor, pageSize = 12, search }: { cursor?: string; pageSize?: number; search?: string } = {}): Promise<APIResponse<CursorPaginatedResponse<Product>>> {
    const params = new URLSearchParams();
    params.append('limit', pageSize.toString());
    if (cursor) {
      params.append('cursor', cursor);
    }
    if (search && search.trim()) {
      params.append('search', search.trim());
    }

    const endpoint = `/products?${params.toString()}`;
    const response = await apiRequest<any>(endpoint);

    if (response.success) {
      const items = response.data.items || response.data.data || [];
      const transformedProducts = items.map(transformApiProduct);

      const transformedData: CursorPaginatedResponse<Product> = {
        data: transformedProducts,
        nextCursor: response.data.next_cursor ?? null,
        total: response.data.total ?? 0,
      };
      return { data: transformedData, success: true };
    }
    return response as APIResponse<CursorPaginatedResponse<Product>>;
  },

  async getById(id: string): Promise<APIResponse<Product | null>> {
    console.log('Fetching product by ID:', id, 'from Square API:', `/products/${id}`);
    const response = await apiRequest<any>(`/products/${encodeURIComponent(id)}`);

    if (response.success) {
      const product = transformApiProduct(response.data);
      return { data: product, success: true };
    }
    return { data: null, success: false, message: response.message };
  },

  async create(data: CreateProductRequest): Promise<APIResponse<Product>> {
    console.log('Creating product via Square API with payload:', data);
    const response = await apiRequest<any>('/products', {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (response.success) {
      const product = transformApiProduct(response.data);
      return { data: product, success: true };
    }
    return response as APIResponse<Product>;
  },

  async update(id: string, data: Partial<CreateProductRequest>): Promise<APIResponse<Product>> {
    console.log('Updating product', id, 'via Square API with payload:', data);
    const response = await apiRequest<any>(`/products/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });

    if (response.success) {
      const product = transformApiProduct(response.data);
      return { data: product, success: true };
    }
    return response as APIResponse<Product>;
  },

  async delete(id: string): Promise<APIResponse<boolean>> {
    console.log('Deleting product via Square API with ID:', id);
    const response = await apiRequest<any>(`/products/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
    return { data: response.success, success: response.success, message: response.message };
  },

  async uploadImages(images: ProductImage[]): Promise<APIResponse<{ image_ids: string[], images: ProductImage[] }>> {
    console.log('Uploading images to Square:', images);
    
    // Filter images that have files to upload
    const imagesToUpload = images.filter(img => img.file && img.url);
    
    if (imagesToUpload.length === 0) {
      return { data: { image_ids: [], images: [] }, success: true };
    }

    // Prepare FormData for multiple image upload
    const formData = new FormData();
    
    imagesToUpload.forEach((image, index) => {
      if (image.file) {
        formData.append('images', image.file, image.file.name);
        formData.append(`image_${index}_type`, image.type);
        formData.append(`image_${index}_label`, image.label || '');
      }
    });

    const response = await apiFileUploadRequest<{ image_ids: string[], images?: any[] }>('/images', formData, 'square');
    
    if (response.success) {
      console.log('Images uploaded successfully:', response.data);
      
      // Transform the response to include proper image objects
      const responseImages: ProductImage[] = [];
      if (response.data?.images && Array.isArray(response.data.images)) {
        responseImages.push(...response.data.images.map((img: any, index: number) => ({
          id: img.id || response.data!.image_ids?.[index] || `${index}`,
          url: img.url || img.image_url || '',
          type: imagesToUpload[index]?.type || 'front',
          label: imagesToUpload[index]?.label || '',
        })));
      } else if (response.data?.image_ids) {
        // Fallback: Create placeholder URLs that can be resolved by image_id
        // In a real implementation, these would be resolved by a separate API call
        responseImages.push(...imagesToUpload.map((img, index) => ({
          id: response.data!.image_ids[index] || img.id,
          url: response.data!.image_ids[index] ? 
               `${SQUARE_API_V2_BASE}/images/${response.data!.image_ids[index]}` : 
               img.url, // Fallback to blob URL if no ID
          type: img.type,
          label: img.label || '',
        })));
      }
      
      return {
        data: { 
          image_ids: response.data?.image_ids || [], 
          images: responseImages 
        }, 
        success: true 
      };
    }
    
    console.error('Failed to upload images:', response.message);
    return {
      data: { image_ids: [], images: [] },
      success: false,
      message: response.message || 'Failed to upload images'
    };
  },

  async uploadTempFilesToSquare(tempFiles: any[]): Promise<APIResponse<{ image_ids: string[], count: number }>> {
    console.log('Uploading temporary files to Square:', tempFiles);

    if (tempFiles.length === 0) {
      return { data: { image_ids: [], count: 0 }, success: true };
    }

    try {
      const url = `${SQUARE_API_V2_BASE}/images/upload-temp-files`;

      const tempHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      const tempToken = localStorage.getItem('auth_token');
      if (tempToken) {
        tempHeaders['Authorization'] = `Bearer ${tempToken}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: tempHeaders,
        body: JSON.stringify(tempFiles),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Temporary files uploaded successfully to Square:', data);

      return {
        data: {
          image_ids: data.image_ids || [],
          count: data.count || 0,
          source: data.source || 'temporary_files',
        },
        success: true,
      };
    } catch (error: any) {
      console.error('Failed to upload temporary files to Square:', error);
      return {
        data: { image_ids: [], count: 0 },
        success: false,
        message: error.message || 'Failed to upload temporary files to Square',
      };
    }
  },
};

// AI API - Updated to use file uploads like the backend
export const aiApi = {
  async analyzeProductImages(): Promise<APIResponse<AIProductSuggestion>> {
    await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate AI processing time

    // Mock AI response based on random selection
    const mockSuggestions: AIProductSuggestion[] = [
      {
        name: 'Premium Cotton Shirt',
        description: 'A high-quality cotton shirt with excellent breathability and comfort. Perfect for both casual and semi-formal occasions.',
        sku: 'PCS-' + Math.random().toString(36).substr(2, 6).toUpperCase(),
        suggestedAttributes: {
          'Material': 'Cotton',
          'Size': 'M',
          'Color': ['Blue'],
        },
        tags: ['premium', 'cotton', 'comfortable', 'versatile'],
      },
      {
        name: 'Classic Denim Jacket',
        description: 'A timeless denim jacket that never goes out of style. Durable construction with a comfortable fit.',
        sku: 'CDJ-' + Math.random().toString(36).substr(2, 6).toUpperCase(),
        suggestedAttributes: {
          'Material': 'Cotton',
          'Size': 'L',
          'Color': ['Blue'],
        },
        tags: ['denim', 'classic', 'outerwear', 'casual'],
      },
    ];

    const randomSuggestion = mockSuggestions[Math.floor(Math.random() * mockSuggestions.length)];
    return { data: randomSuggestion, success: true };
  },

  async executeAIAnalysis(images: ProductImage[]): Promise<APIResponse<AIExecuteResponse>> {
    console.log('Executing AI analysis with images:', images);

    try {
      // Note: AI attributes are now managed separately via the AI attributes API
      // We don't need to load Square attribute definitions here
      const existingAttributes: AttributeDefinition[] = [];

      // Extract SKU from image filenames
      const extractSkuFromImages = (images: ProductImage[]): string => {
        for (const image of images) {
          if (image.file && image.file.name) {
            const filename = image.file.name;
            const match = filename.match(/^([^_]+)_(?:back|detail|front)/i);
            if (match) {
              return match[1];
            }
            const baseName = filename.replace(/\.[^/.]+$/, '');
            const cleanName = baseName.replace(/_(back|detail|front)$/i, '');
            if (cleanName) {
              return cleanName;
            }
          }
        }
        return '';
      };

      // Extract SKU and prefix
      const sku = extractSkuFromImages(images);
      const skuPrefix = sku ? sku.match(/^[A-Z]+/)?.[0] || '' : '';
      
      console.log('Extracted SKU:', sku);
      console.log('Extracted SKU prefix:', skuPrefix);

      // Prepare FormData for file upload (matching backend)
      const formData = new FormData();

      // Add SKU prefix context if available
      if (skuPrefix) {
        formData.append('sku_prefix', skuPrefix);
        console.log('Added SKU prefix to AI request:', skuPrefix);
      }

      // Add images to FormData - support both legacy typed images and generic image slots
      // Also support Google Drive temporary files
      let imageIndex = 0;
      for (const image of images) {
        let fileToUpload: File | null = null;
        let fileName = 'image.jpg';

        // Handle local file uploads
        if (image.file && image.url) {
          fileToUpload = image.file;
          fileName = image.file.name;
        }
        // Handle Google Drive temporary files
        else if (image.temp_path && image.url) {
          try {
            // Fetch the temporary file from the preview URL
            console.log(`Fetching Google Drive temp file for AI analysis: ${image.url}`);
            const response = await fetch(image.url);
            if (response.ok) {
              const blob = await response.blob();
              // Create a File from the Blob
              fileName = image.original_name || image.filename || `image_${image.type}.jpg`;
              fileToUpload = new File([blob], fileName, { type: blob.type || 'image/jpeg' });
              console.log(`Converted temp file to File object: ${fileName}`);
            } else {
              console.error(`Failed to fetch temp file: ${response.status}`);
              continue;
            }
          } catch (error) {
            console.error('Error fetching Google Drive temp file:', error);
            continue;
          }
        }

        // Add file to FormData if we have one
        if (fileToUpload) {
          // For legacy typed images (front/back/detail), use the specific field names
          // For generic 'image' type, use numbered fields (image_1, image_2, etc.)
          if (image.type === 'front') {
            formData.append('front_image', fileToUpload, fileName);
          } else if (image.type === 'back') {
            formData.append('back_image', fileToUpload, fileName);
          } else if (image.type === 'detail') {
            formData.append('detail_image', fileToUpload, fileName);
          } else {
            // Generic image slot - use numbered field
            imageIndex++;
            formData.append(`image_${imageIndex}`, fileToUpload, fileName);
          }
        }
      }

      console.log('AI execute FormData prepared with files:', Array.from(formData.entries()).map(([key, value]) => [key, value instanceof File ? `File: ${value.name} (${value.size} bytes)` : value]));

      // Make the API call using file upload with retry logic
      const response = await retryWithBackoff(() => 
        apiFileUploadRequest<any>('/api/ai/execute', formData, 'ai'),
        2, // maxRetries: frontend does 2 retries (backend already does 3)
        2000, // baseDelay: start with 2 seconds for frontend retries
        (attempt, delay) => {
          console.log(`🔄 AI service busy, waiting ${delay / 1000}s before retry ${attempt}/3...`);
        }
      );

      console.log('Raw AI execute response:', response);

      if (response.success && response.data) {
        // Transform the response and handle attribute creation/updates
        const { response: transformedData, newAttributes } = await transformAIResponse(response.data, existingAttributes);
        console.log('Transformed AI response:', transformedData);
        console.log('New/updated attributes:', newAttributes);
        return { data: transformedData, success: true };
      } else {
        console.error('AI execute failed:', response.message);
        return {
          data: null as any,
          success: false,
          message: response.message || 'AI analysis failed',
        };
      }
    } catch (error) {
      console.error('Error in AI execute analysis:', error);
      return {
        data: null as any,
        success: false,
        message: error instanceof Error ? error.message : 'Failed to execute AI analysis',
      };
    }
  },
};

// Status API
export const statusApi = {
  async getStatus(): Promise<APIResponse<any>> {
    const results = {
      inventory_api: null as any,
      ai_api: null as any,
      overall_status: 'unknown' as 'healthy' | 'partial' | 'unhealthy' | 'unknown',
      environment: 'development',
      timestamp: new Date().toISOString(),
    };

    // Check Square API health (for inventory operations)
    try {
      const inventoryResponse = await apiRequest<any>('/health', {}, 'square');
      if (inventoryResponse.success) {
        results.inventory_api = {
          status: 'healthy',
          url: SQUARE_API_URL,
          data: inventoryResponse.data,
        };
      } else {
        results.inventory_api = {
          status: 'unhealthy',
          url: SQUARE_API_URL,
          error: inventoryResponse.message,
        };
      }
    } catch (error) {
      results.inventory_api = {
        status: 'unhealthy',
        url: SQUARE_API_URL,
        error: error instanceof Error ? error.message : 'Connection failed',
      };
    }

    // Check AI API health
    try {
      const aiResponse = await apiRequest<any>('/health', {}, 'ai');
      if (aiResponse.success) {
        results.ai_api = {
          status: 'healthy',
          url: AI_API_URL,
          data: aiResponse.data,
        };
      } else {
        results.ai_api = {
          status: 'unhealthy',
          url: AI_API_URL,
          error: aiResponse.message,
        };
      }
    } catch (error) {
      results.ai_api = {
        status: 'unhealthy',
        url: AI_API_URL,
        error: error instanceof Error ? error.message : 'Connection failed',
      };
    }

    // Determine overall status
    const inventoryHealthy = results.inventory_api?.status === 'healthy';
    const aiHealthy = results.ai_api?.status === 'healthy';

    if (inventoryHealthy && aiHealthy) {
      results.overall_status = 'healthy';
    } else if (inventoryHealthy || aiHealthy) {
      results.overall_status = 'partial';
    } else {
      results.overall_status = 'unhealthy';
    }

    // Extract environment from either API response
    if (results.inventory_api?.data?.environment) {
      results.environment = results.inventory_api.data.environment;
    } else if (results.ai_api?.data?.environment) {
      results.environment = results.ai_api.data.environment;
    }

    return {
      data: results,
      success: true,
      message: `Overall status: ${results.overall_status}`,
    };
  },
  
  // Dropdown configuration endpoints
  async getDropdownConfig(organizationId: string): Promise<APIResponse<any>> {
    return apiRequest<any>(`/organizations/${organizationId}/dropdown-config`, {
      method: 'GET',
    }, 'ai');
  },

  async updateDropdownConfig(organizationId: string, config: any): Promise<APIResponse<any>> {
    return apiRequest<any>(`/organizations/${organizationId}/dropdown-config`, {
      method: 'PUT',
      body: JSON.stringify(config),
    }, 'ai');
  },

  // Label print settings endpoints (SC-081)
  async getLabelPrintSettings(organizationId: string): Promise<APIResponse<LabelPrintSettings | null>> {
    return apiRequest<LabelPrintSettings | null>(`/organizations/${organizationId}/label-print-settings`, {
      method: 'GET',
    }, 'ai');
  },

  async updateLabelPrintSettings(organizationId: string, settings: LabelPrintSettings): Promise<APIResponse<LabelPrintSettings>> {
    return apiRequest<LabelPrintSettings>(`/organizations/${organizationId}/label-print-settings`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    }, 'ai');
  },

  // Enum Definitions API
  enumDefinitions: {
    /**
     * List all enum definitions for an organization
     * @param type Optional filter by type (e.g., "categories", "eras")
     * @param organizationId Optional organization ID (defaults to current user's org)
     */
    async list(type?: string, organizationId?: string): Promise<APIResponse<EnumDefinition[]>> {
      const params = new URLSearchParams();
      if (type) params.append('type', type);
      if (organizationId) params.append('organization_id', organizationId);
      const queryString = params.toString();
      const url = queryString ? `/enum-definitions/?${queryString}` : '/enum-definitions/';
      return apiRequest<EnumDefinition[]>(url, {
        method: 'GET',
      }, 'ai');
    },

    /**
     * Create a new enum definition
     */
    async create(definition: EnumDefinitionCreate, organizationId?: string): Promise<APIResponse<EnumDefinition>> {
      const params = new URLSearchParams();
      if (organizationId) params.append('organization_id', organizationId);
      const queryString = params.toString();
      const url = queryString ? `/enum-definitions/?${queryString}` : '/enum-definitions/';
      return apiRequest<EnumDefinition>(url, {
        method: 'POST',
        body: JSON.stringify(definition),
      }, 'ai');
    },

    /**
     * Update an existing enum definition
     */
    async update(id: string, updates: EnumDefinitionUpdate): Promise<APIResponse<EnumDefinition>> {
      return apiRequest<EnumDefinition>(`/enum-definitions/${id}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      }, 'ai');
    },

    /**
     * Delete an enum definition
     */
    async delete(id: string): Promise<APIResponse<{ status: string; id: string }>> {
      return apiRequest<{ status: string; id: string }>(`/enum-definitions/${id}`, {
        method: 'DELETE',
      }, 'ai');
    },

    /**
     * Bulk create/update definitions (useful for imports)
     */
    async bulkUpsert(definitions: EnumDefinitionCreate[]): Promise<APIResponse<BulkUpsertResponse>> {
      return apiRequest<BulkUpsertResponse>('/enum-definitions/bulk-upsert', {
        method: 'POST',
        body: JSON.stringify({ definitions }),
      }, 'ai');
    },
  },

};